import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Save, RotateCcw, Loader2, Upload, FolderOpen, ExternalLink } from 'lucide-react';
import { MediaPickerModal } from './MediaLibraryTab';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

function ImageField({ field, value, onChange, headers }) {
  const [picker, setPicker] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/admin/media/upload`, fd, { headers });
      onChange(res.data.url.startsWith('http') ? res.data.url : `${BACKEND}${res.data.url}`);
    } catch (err) {
      console.error(err);
      alert(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  return (
    <div className="flex gap-4 items-start">
      <div className="w-24 h-24 flex-shrink-0 rounded-sm overflow-hidden border border-white/10 bg-black">
        {value && <img src={value} alt={field.label} className="w-full h-full object-cover" />}
      </div>
      <div className="flex-1 space-y-2">
        <input
          type="text"
          value={value}
          onChange={e => onChange(e.target.value)}
          className="w-full bg-[#1a1a1a] border border-white/10 focus:border-[#D4AF37] focus:outline-none px-3 py-2 text-white/70 font-manrope text-xs rounded-sm"
          placeholder="Image URL"
          data-testid={`editor-input-${field.key}`}
        />
        <div className="flex gap-2">
          <label className="flex items-center gap-1.5 border border-white/10 hover:border-[#D4AF37]/40 text-white/50 hover:text-[#D4AF37] px-3 py-1.5 rounded-sm font-manrope text-[10px] uppercase tracking-widest cursor-pointer transition-colors" data-testid={`editor-upload-${field.key}`}>
            {uploading ? <Loader2 size={11} className="animate-spin" /> : <Upload size={11} />} Upload New
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} />
          </label>
          <button type="button" onClick={() => setPicker(true)} className="flex items-center gap-1.5 border border-white/10 hover:border-[#D4AF37]/40 text-white/50 hover:text-[#D4AF37] px-3 py-1.5 rounded-sm font-manrope text-[10px] uppercase tracking-widest transition-colors" data-testid={`editor-library-${field.key}`}>
            <FolderOpen size={11} /> Media Library
          </button>
          {value && (
            <a href={value} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-white/30 hover:text-white/60 px-2 py-1.5 font-manrope text-[10px] uppercase tracking-widest transition-colors">
              <ExternalLink size={11} /> View
            </a>
          )}
        </div>
      </div>
      {picker && (
        <MediaPickerModal
          headers={headers}
          onSelect={(url) => { onChange(url); setPicker(false); }}
          onClose={() => setPicker(false)}
        />
      )}
    </div>
  );
}

function ContentField({ field, headers, onSaved }) {
  const [value, setValue] = useState(field.value);
  const [saving, setSaving] = useState(false);
  const dirty = value !== field.value;

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/content/${field.key}`, { value }, { headers });
      onSaved();
    } catch (err) { console.error(err); } finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm('Reset this field to its original default?')) return;
    try {
      await axios.delete(`${API}/admin/content/${field.key}`, { headers });
      onSaved();
    } catch (err) { console.error(err); }
  };

  return (
    <div className="bg-[#16161a] border border-white/5 rounded-sm p-5" data-testid={`editor-field-${field.key}`}>
      <div className="flex items-center justify-between mb-3">
        <label className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-manrope">
          {field.label}
          {!field.is_default && <span className="ml-2 text-[#D4AF37]/70 normal-case tracking-normal">(edited)</span>}
        </label>
        <div className="flex items-center gap-2">
          {!field.is_default && (
            <button onClick={reset} className="flex items-center gap-1 text-white/30 hover:text-white/60 font-manrope text-[10px] uppercase tracking-widest transition-colors" data-testid={`editor-reset-${field.key}`}>
              <RotateCcw size={10} /> Reset
            </button>
          )}
          {dirty && (
            <button onClick={save} disabled={saving} className="flex items-center gap-1.5 bg-[#D4AF37] text-black font-manrope font-bold text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-sm hover:bg-[#C5A059] transition-colors disabled:opacity-50" data-testid={`editor-save-${field.key}`}>
              {saving ? <Loader2 size={10} className="animate-spin" /> : <Save size={10} />} Save
            </button>
          )}
        </div>
      </div>
      {field.type === 'image' ? (
        <ImageField field={field} value={value} onChange={setValue} headers={headers} />
      ) : field.type === 'textarea' ? (
        <textarea rows={3} value={value} onChange={e => setValue(e.target.value)}
          className="w-full bg-[#1a1a1a] border border-white/10 focus:border-[#D4AF37] focus:outline-none px-3 py-2.5 text-white font-manrope text-sm rounded-sm resize-none"
          data-testid={`editor-input-${field.key}`} />
      ) : (
        <input type="text" value={value} onChange={e => setValue(e.target.value)}
          className="w-full bg-[#1a1a1a] border border-white/10 focus:border-[#D4AF37] focus:outline-none px-3 py-2.5 text-white font-manrope text-sm rounded-sm"
          data-testid={`editor-input-${field.key}`} />
      )}
    </div>
  );
}

export function WebsiteEditorTab({ headers }) {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activePage, setActivePage] = useState('Home');

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/content`, { headers });
      setFields(res.data);
    } catch (err) { console.error(err); } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const pages = [...new Set(fields.map(f => f.page))];
  const pageFields = fields.filter(f => f.page === activePage);

  if (loading) return <div className="flex justify-center py-16"><Loader2 size={22} className="text-[#D4AF37] animate-spin" /></div>;

  return (
    <div data-testid="website-editor-tab">
      <div className="mb-6">
        <h3 className="font-playfair text-xl text-white mb-1">Website Editor</h3>
        <p className="text-white/30 font-manrope text-xs">Edit the text and images shown on the public website. Changes go live immediately after saving.</p>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {pages.map(p => (
          <button key={p} onClick={() => setActivePage(p)}
            className={`px-4 py-2 rounded-sm font-manrope text-xs uppercase tracking-widest border transition-colors ${activePage === p ? 'bg-[#D4AF37] text-black border-[#D4AF37] font-bold' : 'text-white/50 border-white/10 hover:border-white/30'}`}
            data-testid={`editor-page-${p.toLowerCase()}`}>
            {p}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {pageFields.map(f => (
          <ContentField key={`${f.key}-${f.value}`} field={f} headers={headers} onSaved={load} />
        ))}
      </div>
    </div>
  );
}
